<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <div class="d-flex align-center mb-4">
      <v-icon color="primary" size="32" class="mr-3">mdi-clipboard-check-multiple</v-icon>
      <div>
        <h1 class="text-h5 mb-0">Auditoría de inventario en vivo</h1>
        <span class="grey--text text-body-2">
          Escanea un producto, compara el conteo físico contra el sistema y ajusta al instante.
        </span>
      </div>
      <v-spacer></v-spacer>
      <!-- Authorizer status -->
      <v-chip
        v-if="selectedAuthorizer"
        color="success"
        outlined
        class="mr-2"
      >
        <v-icon left small>mdi-account-check</v-icon>
        {{ selectedAuthorizer.user_name || selectedAuthorizer.user_email }}
      </v-chip>
      <v-btn
        :color="selectedAuthorizer ? 'grey' : 'primary'"
        outlined
        @click="openAuthorizerDialog"
        :loading="loading_users"
      >
        <v-icon left>mdi-account-switch</v-icon>
        {{ selectedAuthorizer ? "Cambiar autorizador" : "Autorizar sesión" }}
      </v-btn>
    </div>

    <!-- KPIs -->
    <v-row class="mb-2" dense>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 text-center">
          <div class="text-h5 font-weight-bold">{{ rows.length }}</div>
          <div class="caption grey--text">Productos contados</div>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 text-center">
          <div class="text-h5 font-weight-bold success--text">{{ kpis.cuadrados }}</div>
          <div class="caption grey--text">Cuadrados</div>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 text-center">
          <div class="text-h5 font-weight-bold warning--text">{{ kpis.sobrantes }}</div>
          <div class="caption grey--text">Sobrantes</div>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="pa-3 text-center">
          <div class="text-h5 font-weight-bold error--text">{{ kpis.faltantes }}</div>
          <div class="caption grey--text">Faltantes</div>
        </v-card>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>
        <v-icon left>mdi-barcode-scan</v-icon>
        Conteo
      </v-tab>
      <v-tab>
        <v-icon left>mdi-history</v-icon>
        Movimientos
      </v-tab>
      <v-tab>
        <v-icon left>mdi-chart-donut</v-icon>
        Reporte
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- ================= TAB 1: CONTEO ================= -->
      <v-tab-item>
        <v-card outlined class="pa-4 mb-4">
          <div class="d-flex align-center flex-wrap">
            <v-text-field
              ref="barcodeInput"
              v-model="barcode"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-barcode-scan"
              placeholder="Escanear código de barras"
              class="mr-3"
              style="max-width: 320px"
              :loading="loading"
              @keyup.enter="onScan"
            ></v-text-field>
            <v-text-field
              v-model="auditFrom"
              label="Auditoría desde"
              type="date"
              :max="today"
              dense
              outlined
              hide-details
              prepend-inner-icon="mdi-calendar-start"
              class="mr-3"
              style="max-width: 200px"
              @change="onAuditFromChange"
            ></v-text-field>
            <span class="grey--text text-body-2">
              Escanea el mismo código para sumar unidades, o edita el conteo físico manualmente.
              Los productos auditados desde la fecha indicada se avisan al reescanear.
            </span>
          </div>
        </v-card>

        <!-- Current product card -->
        <v-card v-if="currentRow" outlined class="pa-4 mb-4">
          <v-alert
            v-if="currentRow.priorAudit"
            :type="currentRow.priorAudit.adjusted ? 'info' : 'warning'"
            dense
            text
            class="mb-3"
          >
            <v-icon left small>mdi-history</v-icon>
            Este producto ya fue auditado {{ priorAuditLabel(currentRow.priorAudit) }} (contado
            {{ currentRow.priorAudit.cantidad }}, sistema
            {{ currentRow.priorAudit.inventario }}<span v-if="currentRow.priorAudit.pos">
              · {{ currentRow.priorAudit.pos }}</span
            >)<span v-if="currentRow.priorAudit.adjusted"> · ya se creó un ajuste</span>.
          </v-alert>
          <v-row align="center">
            <v-col cols="12" md="5">
              <div class="text-h6">{{ currentRow.nombre }}</div>
              <div class="grey--text text-body-2">
                {{ currentRow.refe }} · EAN {{ currentRow.ean }}
              </div>
              <div class="grey--text text-caption" v-if="currentRow.marca || currentRow.categoria">
                {{ currentRow.marca }} {{ currentRow.categoria ? "· " + currentRow.categoria : "" }}
              </div>
              <div class="mt-2 d-flex align-center flex-wrap">
                <v-chip color="primary" label small class="mr-2 mb-1">
                  <v-icon left x-small>mdi-tag</v-icon>
                  Precio: {{ formatCurrency(currentRow.precio) }}
                </v-chip>
                <v-chip color="grey darken-1" label small dark outlined class="mr-2 mb-1">
                  <v-icon left x-small>mdi-cash</v-icon>
                  Costo: {{ formatCurrency(currentRow.costo) }}
                </v-chip>
                <v-chip color="grey darken-3" label small dark class="mb-1">
                  <v-icon left x-small>mdi-cash-multiple</v-icon>
                  Costo total: {{ formatCurrency(costTotal(currentRow.costo, currentRow.countedQty)) }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="6" md="2" class="text-center">
              <div class="text-caption grey--text">Stock sistema</div>
              <div class="text-h4">{{ currentRow.systemQty }}</div>
            </v-col>
            <v-col cols="6" md="2" class="text-center">
              <div class="text-caption grey--text">Conteo físico</div>
              <v-text-field
                :value="currentRow.countedQty"
                type="number"
                min="0"
                dense
                outlined
                hide-details
                class="centered-input"
                @change="setCount(currentRow, $event)"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="text-center">
              <div class="text-caption grey--text">Diferencia</div>
              <v-chip :color="diffColor(currentRow)" dark large class="mt-1">
                {{ diffLabel(currentRow) }}
              </v-chip>
              <div class="mt-2">
                <v-btn
                  small
                  text
                  color="primary"
                  @click="showMovements(currentRow)"
                >
                  <v-icon left small>mdi-history</v-icon>
                  Movimientos
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Session table -->
        <v-card outlined>
          <v-toolbar flat dense>
            <v-icon class="mr-2">mdi-format-list-checks</v-icon>
            <span class="text-subtitle-2">Productos de la sesión</span>
            <v-spacer></v-spacer>
            <v-btn
              small
              outlined
              color="success"
              :disabled="rows.length === 0 || rows.every((r) => r.verified)"
              @click="markAllVerified"
            >
              <v-icon left small>mdi-check-all</v-icon>
              Marcar todos como verificados
            </v-btn>
          </v-toolbar>
          <v-divider></v-divider>
          <v-data-table
            :headers="sessionHeaders"
            :items="rows"
            item-key="ean"
            :items-per-page="15"
            class="minimal-table"
            no-data-text="Aún no has escaneado productos"
          >
            <template v-slot:[`item.nombre`]="{ item }">
              <div class="font-weight-medium">{{ item.nombre }}</div>
              <div class="grey--text text-caption">{{ item.refe }} · {{ item.ean }}</div>
            </template>
            <template v-slot:[`item.precio`]="{ item }">
              <span class="font-weight-medium primary--text">{{ formatCurrency(item.precio) }}</span>
            </template>
            <template v-slot:[`item.costo`]="{ item }">
              <span class="grey--text">{{ formatCurrency(item.costo) }}</span>
            </template>
            <template v-slot:[`item.costoTotal`]="{ item }">
              <span class="font-weight-medium">
                {{ formatCurrency(costTotal(item.costo, item.countedQty)) }}
              </span>
            </template>
            <template v-slot:[`item.systemQty`]="{ item }">
              <span>{{ item.systemQty }}</span>
            </template>
            <template v-slot:[`item.countedQty`]="{ item }">
              <v-text-field
                :value="item.countedQty"
                type="number"
                min="0"
                dense
                outlined
                hide-details
                style="max-width: 90px"
                class="centered-input mx-auto"
                @change="setCount(item, $event)"
              ></v-text-field>
            </template>
            <template v-slot:[`item.difference`]="{ item }">
              <v-chip :color="diffColor(item)" dark small>{{ diffLabel(item) }}</v-chip>
              <v-icon
                v-if="item.verified"
                color="success"
                small
                class="ml-1"
                title="Verificado"
              >
                mdi-check-decagram
              </v-icon>
              <v-icon
                v-if="item.priorAudit"
                :color="item.priorAudit.adjusted ? 'info' : 'warning'"
                small
                class="ml-1"
                :title="
                  (item.priorAudit.adjusted ? 'Ya auditado y ajustado ' : 'Ya auditado ') +
                  priorAuditLabel(item.priorAudit)
                "
              >
                mdi-history
              </v-icon>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="showMovements(item)">
                    <v-list-item-icon class="mr-3">
                      <v-icon>mdi-history</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Ver movimientos</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item :disabled="item.verified" @click="markVerified(item)">
                    <v-list-item-icon class="mr-3">
                      <v-icon :color="item.verified ? 'success' : ''">mdi-check-decagram</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.verified ? "Verificado ✓" : "Marcar verificado" }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    :disabled="diffOf(item) === 0 || item.adjusted"
                    @click="openAdjust(item)"
                  >
                    <v-list-item-icon class="mr-3">
                      <v-icon :color="item.adjusted ? 'success' : 'warning'">
                        {{ item.adjusted ? "mdi-check-decagram" : "mdi-scale-balance" }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.adjusted ? "Ajuste creado" : "Crear ajuste" }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider></v-divider>

                  <v-list-item @click="removeRow(item)">
                    <v-list-item-icon class="mr-3">
                      <v-icon color="red">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="red--text">Quitar de la lista</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ================= TAB 2: MOVIMIENTOS ================= -->
      <v-tab-item>
        <v-card outlined class="pa-4 mb-4">
          <div class="d-flex align-center flex-wrap">
            <div v-if="movEan" class="mr-4">
              <div class="text-subtitle-1 font-weight-medium">{{ movName }}</div>
              <div class="grey--text text-caption">EAN {{ movEan }}</div>
            </div>
            <div v-else class="grey--text">
              Selecciona un producto (escanéalo o usa el botón de movimientos) para ver su historial.
            </div>
            <v-spacer></v-spacer>
            <v-menu :close-on-content-click="false" offset-y>
              <template v-slot:activator="{ on }">
                <v-btn outlined small v-on="on" class="mr-2">
                  <v-icon left small>mdi-calendar</v-icon>
                  {{ dateFrom }} → {{ dateTo }}
                </v-btn>
              </template>
              <v-card class="pa-3" width="280">
                <v-text-field v-model="dateFrom" label="Desde" type="date" dense outlined hide-details class="mb-2" />
                <v-text-field v-model="dateTo" label="Hasta" type="date" dense outlined hide-details class="mb-2" />
                <v-btn block color="primary" small @click="reloadMovements">Aplicar</v-btn>
              </v-card>
            </v-menu>
          </div>
          <v-chip-group v-model="movFilter" column class="mt-2">
            <v-chip filter value="all" small>Todos</v-chip>
            <v-chip filter value="Compra" small>Compras</v-chip>
            <v-chip filter value="Venta" small>Ventas</v-chip>
            <v-chip filter value="Ajuste" small>Ajustes</v-chip>
          </v-chip-group>
        </v-card>

        <v-card outlined>
          <v-data-table
            :headers="movHeaders"
            :items="filteredMovements"
            :loading="movLoading"
            :items-per-page="20"
            no-data-text="Sin movimientos para este producto en el rango seleccionado"
            class="minimal-table"
          >
            <template v-slot:[`item.order_number`]="{ item }">
              <v-icon :color="getEventColor(item.order_number, item.quantity)" small class="mr-1">
                {{ getEventIcon(item.order_number, item.quantity) }}
              </v-icon>
              {{ item.order_number }}
            </template>
            <template v-slot:[`item.tipo`]="{ item }">
              <v-chip :color="getEventColor(item.order_number, item.quantity)" dark x-small>
                {{ getEventName(item.order_number, item.quantity) }}
              </v-chip>
            </template>
            <template v-slot:[`item.quantity`]="{ item }">
              {{ item.quantity }}
            </template>
            <template v-slot:[`item.tcost`]="{ item }">
              {{ formatCurrency(item.tcost) }}
            </template>
            <template v-slot:[`item.tprice`]="{ item }">
              {{ formatCurrency(item.tprice) }}
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ================= TAB 3: REPORTE DEL DÍA ================= -->
      <v-tab-item>
        <v-card outlined class="pa-4 mb-4">
          <div class="d-flex align-center flex-wrap">
            <v-text-field
              v-model="reportDateFrom"
              label="Desde"
              type="date"
              dense
              outlined
              hide-details
              style="max-width: 170px"
              class="mr-2"
              @change="loadReport"
            ></v-text-field>
            <v-text-field
              v-model="reportDateTo"
              label="Hasta"
              type="date"
              dense
              outlined
              hide-details
              style="max-width: 170px"
              class="mr-3"
              @change="loadReport"
            ></v-text-field>
            <v-btn color="primary" small outlined class="mr-2" @click="loadReport" :loading="reportLoading">
              <v-icon left small>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="green darken-1"
              small
              outlined
              :disabled="(reportView === 'pending' ? pendingCount : verifiedToday.length) === 0"
              @click="exportReport"
            >
              <v-icon left small>mdi-microsoft-excel</v-icon>
              Exportar {{ reportView === "pending" ? "pendientes" : "verificados" }}
            </v-btn>
          </div>
        </v-card>

        <!-- Report KPIs -->
        <v-row class="mb-2" dense>
          <v-col cols="6" md="2">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold">{{ verifiedToday.length }}</div>
              <div class="caption grey--text">Verificados</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="2">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold">{{ storeStockCount }}</div>
              <div class="caption grey--text">SKUs con stock</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="2">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold error--text">{{ reportWithDiff }}</div>
              <div class="caption grey--text">Con diferencia</div>
            </v-card>
          </v-col>
          <v-col cols="6" md="2">
            <v-card outlined class="pa-3 text-center">
              <div class="text-h5 font-weight-bold warning--text">{{ pendingCount }}</div>
              <div class="caption grey--text">Pendientes</div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-3">
              <div class="caption grey--text mb-1 text-center">Inventario auditado</div>
              <v-progress-linear
                :value="auditPct"
                :color="auditPctColor"
                height="22"
                rounded
              >
                <strong>{{ auditPct }}%</strong>
              </v-progress-linear>
            </v-card>
          </v-col>
        </v-row>

        <div class="mb-2">
          <v-btn-toggle v-model="reportView" mandatory dense color="primary">
            <v-btn value="verified" small>
              <v-icon left small>mdi-check-decagram</v-icon>
              Verificados ({{ verifiedToday.length }})
            </v-btn>
            <v-btn value="pending" small>
              <v-icon left small>mdi-clipboard-alert-outline</v-icon>
              Pendientes ({{ pendingCount }})
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Verificados -->
        <v-card v-if="reportView === 'verified'" outlined>
          <v-data-table
            :headers="reportHeaders"
            :items="verifiedToday"
            :loading="reportLoading"
            :items-per-page="20"
            no-data-text="No hay productos verificados en el rango seleccionado"
            class="minimal-table"
          >
            <template v-slot:[`item.nombre`]="{ item }">
              <div class="font-weight-medium">{{ item.nombre }}</div>
              <div class="grey--text text-caption">{{ item.refe }} · {{ item.ean }}</div>
            </template>
            <template v-slot:[`item.fecha`]="{ item }">
              {{ (item.fecha || "").substring(0, 10) }}
            </template>
            <template v-slot:[`item.costo`]="{ item }">
              <span class="grey--text">{{ formatCurrency(item.costo) }}</span>
            </template>
            <template v-slot:[`item.costoTotal`]="{ item }">
              <span class="font-weight-medium">
                {{ formatCurrency(costTotal(item.costo, item.cantidad)) }}
              </span>
            </template>
            <template v-slot:[`item.diferencia`]="{ item }">
              <v-chip :color="reportDiffColor(item)" dark small>
                {{ reportDiffLabel(item) }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card>

        <!-- Pendientes por auditar -->
        <v-card v-else outlined>
          <v-data-table
            :headers="pendingHeaders"
            :items="pendingItems"
            :loading="reportLoading"
            :items-per-page="20"
            no-data-text="No hay productos pendientes por auditar"
            class="minimal-table"
          >
            <template v-slot:[`item.nombre`]="{ item }">
              <div class="font-weight-medium">{{ item.nombre }}</div>
              <div class="grey--text text-caption">{{ item.refe }} · {{ item.ean }}</div>
            </template>
            <template v-slot:[`item.cantidad`]="{ item }">
              {{ Math.round(parseFloat(item.cantidad) || 0) }}
            </template>
            <template v-slot:[`item.costo`]="{ item }">
              <span class="grey--text">{{ formatCurrency(item.costo) }}</span>
            </template>
            <template v-slot:[`item.costoTotal`]="{ item }">
              <span class="font-weight-medium">
                {{ formatCurrency(costTotal(item.costo, Math.round(parseFloat(item.cantidad) || 0))) }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- ================= ADJUSTMENT DIALOG ================= -->
    <v-dialog v-model="adjust_dialog" max-width="480px" persistent>
      <v-card v-if="adjustRow">
        <v-card-title class="pa-0">
          <v-toolbar flat dark :color="adjustDirection === 'in' ? 'warning' : 'error'">
            <v-icon class="mr-3">mdi-scale-balance</v-icon>
            <v-toolbar-title>
              Ajuste por {{ adjustDirection === "in" ? "sobrante" : "faltante" }}
            </v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-medium">{{ adjustRow.nombre }}</div>
            <div class="grey--text text-caption">{{ adjustRow.refe }} · EAN {{ adjustRow.ean }}</div>
          </div>
          <v-row class="text-center mb-2">
            <v-col cols="4">
              <div class="text-caption grey--text">Sistema</div>
              <div class="text-h6">{{ adjustRow.systemQty }}</div>
            </v-col>
            <v-col cols="4">
              <div class="text-caption grey--text">Físico</div>
              <div class="text-h6">{{ adjustRow.countedQty }}</div>
            </v-col>
            <v-col cols="4">
              <div class="text-caption grey--text">
                {{ adjustDirection === "in" ? "Se ingresan" : "Se retiran" }}
              </div>
              <div class="text-h6" :class="adjustDirection === 'in' ? 'warning--text' : 'error--text'">
                {{ Math.abs(diffOf(adjustRow)) }}
              </div>
            </v-col>
          </v-row>
          <v-alert
            v-if="adjustRow.priorAudit && adjustRow.priorAudit.adjusted"
            type="warning"
            dense
            text
            class="mb-2"
          >
            Este producto ya tiene un ajuste hoy. Verifica antes de crear otro.
          </v-alert>
          <v-form ref="adjustForm">
            <v-select
              v-model="adjustMotive"
              :items="adjustDirection === 'in' ? FP_in : FP_out"
              item-text="valor"
              return-object
              label="Motivo del ajuste"
              outlined
              dense
              :rules="[(v) => !!v || 'Seleccione un motivo']"
              prepend-inner-icon="mdi-tag-outline"
            ></v-select>
          </v-form>
          <v-alert v-if="!selectedAuthorizer" type="warning" dense text class="mb-0">
            Debes autorizar la sesión antes de crear ajustes.
          </v-alert>
          <div v-else class="grey--text text-caption">
            Autoriza: {{ selectedAuthorizer.user_name || selectedAuthorizer.user_email }}
          </div>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="adjust_dialog = false">Cancelar</v-btn>
          <v-btn
            :color="adjustDirection === 'in' ? 'warning' : 'error'"
            dark
            :loading="savingAdjust"
            :disabled="!selectedAuthorizer"
            @click="confirmAdjust"
          >
            <v-icon left>mdi-check</v-icon>
            Crear ajuste
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= AUTHORIZER DIALOG ================= -->
    <v-dialog v-model="authorizer_dialog" max-width="600px">
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat dark color="primary">
            <v-icon class="mr-3">mdi-account-check</v-icon>
            <v-toolbar-title>Seleccionar Autorizador</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon dark @click="closeAuthorizerDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-list v-if="authorizedUsers.length > 0">
            <template v-for="(user, index) in authorizedUsers">
              <v-list-item
                :key="user.user_email"
                @click="selectAuthorizerForValidation(user)"
                :class="{
                  'blue lighten-5':
                    selectedAuthorizer &&
                    selectedAuthorizer.user_email === user.user_email,
                }"
              >
                <v-list-item-avatar>
                  <v-icon :color="user.user_access === 'OWNER' ? 'primary' : 'success'">
                    {{ user.user_access === "OWNER" ? "mdi-crown" : "mdi-shield-account" }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ user.user_name || user.user_email }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ user.user_email }}
                    <v-chip
                      x-small
                      class="ml-2"
                      :color="user.user_access === 'OWNER' ? 'primary' : 'success'"
                    >
                      {{ user.user_access }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon
                    v-if="
                      selectedAuthorizer &&
                      selectedAuthorizer.user_email === user.user_email
                    "
                    color="success"
                  >
                    mdi-check-circle
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-divider
                v-if="index < authorizedUsers.length - 1"
                :key="`divider-${user.user_email}`"
              ></v-divider>
            </template>
          </v-list>
          <div v-else class="text-center py-12">
            <v-progress-circular
              v-if="loading_users"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <template v-else>
              <v-icon size="64" color="grey lighten-2">mdi-account-off</v-icon>
              <h3 class="text-h6 mt-4 grey--text">
                No hay usuarios autorizados disponibles
              </h3>
              <p class="grey--text mt-2">No se encontraron usuarios con rol ADMIN o OWNER</p>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ================= PASSWORD DIALOG ================= -->
    <v-dialog v-model="password_dialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat dark color="secondary">
            <v-icon class="mr-3">mdi-lock-check</v-icon>
            <v-toolbar-title>Confirmar Autorización</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="text-center mb-4">
            <v-icon size="64" color="secondary">mdi-shield-lock</v-icon>
            <h3 class="text-h6 mt-3">Verificación de Seguridad</h3>
            <p class="text-body-2 grey--text mt-2">
              Por favor ingrese la contraseña de<br />
              <strong>{{
                tempAuthorizer ? tempAuthorizer.user_name || tempAuthorizer.user_email : ""
              }}</strong>
            </p>
          </div>
          <v-form ref="passwordForm" @submit.prevent="validatePassword">
            <v-text-field
              v-model="authorizerPassword"
              label="Contraseña"
              type="password"
              outlined
              prepend-inner-icon="mdi-lock"
              :rules="[(v) => !!v || 'La contraseña es requerida']"
              :error-messages="passwordError"
              @input="passwordError = ''"
              autofocus
              @keyup.enter="validatePassword"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelPasswordValidation">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="validatePassword"
            :loading="validatingPassword"
            :disabled="!authorizerPassword"
          >
            <v-icon left>mdi-check</v-icon>
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "@/services/webserver.js";
import { classifyEvent, groupEventsByEan } from "@/views/Auditoria/utils/closureEngine.js";
import * as XLSX from "xlsx";

export default {
  name: "AuditoriaEnVivo",
  data() {
    return {
      activeTab: 0,

      // Scan / session
      barcode: "",
      loading: false,
      rows: [], // { ean, refe, nombre, marca, categoria, grupo, color, talla, precio, costo, systemQty, countedQty, adjusted, raw }
      currentEan: null,

      // Authorization
      users: [],
      loading_users: false,
      authorizer_dialog: false,
      password_dialog: false,
      validatingPassword: false,
      selectedAuthorizer: null,
      tempAuthorizer: null,
      authorizerPassword: "",
      passwordError: "",

      // Motives
      FP_in: [], // AJ-ENTRADA (sobrantes)
      FP_out: [], // AJ-SALIDA (faltantes)

      // Adjustment dialog
      adjust_dialog: false,
      adjustRow: null,
      adjustMotive: null,
      savingAdjust: false,

      // Movements
      movEan: null,
      movName: "",
      movLoading: false,
      movFilter: "all",
      movCache: {}, // { ean: [events] }
      movements: [],
      dateFrom: "",
      dateTo: "",

      // Table headers
      sessionHeaders: [
        { text: "Producto", value: "nombre" },
        { text: "Precio", value: "precio", align: "right" },
        { text: "Costo", value: "costo", align: "right" },
        { text: "Costo total", value: "costoTotal", align: "right", sortable: false },
        { text: "Stock sistema", value: "systemQty", align: "center" },
        { text: "Conteo físico", value: "countedQty", align: "center", sortable: false },
        { text: "Diferencia", value: "difference", align: "center", sortable: false },
        { text: "Acciones", value: "actions", align: "center", sortable: false },
      ],
      movHeaders: [
        { text: "Fecha", value: "order_date" },
        { text: "Documento", value: "order_number" },
        { text: "Tipo", value: "tipo", sortable: false },
        { text: "Cantidad", value: "quantity", align: "right" },
        { text: "Costo", value: "tcost", align: "right" },
        { text: "Precio", value: "tprice", align: "right" },
      ],

      // Período de auditoría: desde `auditFrom` hasta hoy (se recuerda en localStorage)
      today: getToday(),
      auditFrom: getToday(),
      // Auditados en el período (tabla central `auditoria`):
      // ean -> { _id, fecha, cantidad, inventario, adjusted, pos }
      auditedByEan: {},

      // Consolidated report (rango de fechas)
      reportDateFrom: "",
      reportDateTo: "",
      reportLoading: false,
      reportView: "verified", // 'verified' | 'pending'
      verifiedToday: [], // registros de `auditoria` del rango (dedup por ean, gana el mayor id)
      storeItems: [], // SKUs con stock en la tienda (lista congelada)
      storeStockCount: 0, // SKUs con stock en la tienda (denominador)
      storeCountLoaded: false,
      reportHeaders: [
        { text: "Producto", value: "nombre" },
        { text: "Fecha", value: "fecha" },
        { text: "Marca", value: "marca" },
        { text: "Contado", value: "cantidad", align: "center" },
        { text: "Sistema", value: "inventario", align: "center" },
        { text: "Costo unit.", value: "costo", align: "right" },
        { text: "Costo total", value: "costoTotal", align: "right", sortable: false },
        { text: "Diferencia", value: "diferencia", align: "center", sortable: false },
        { text: "Auditado por", value: "pos" },
      ],
      pendingHeaders: [
        { text: "Producto", value: "nombre" },
        { text: "Marca", value: "marca" },
        { text: "Stock sistema", value: "cantidad", align: "center" },
        { text: "Costo unit.", value: "costo", align: "right" },
        { text: "Costo total", value: "costoTotal", align: "right", sortable: false },
      ],

      snackbar: { show: false, text: "", color: "info", timeout: 3000 },
    };
  },

  computed: {
    currentRow() {
      return this.rows.find((r) => r.ean === this.currentEan) || null;
    },
    authorizedUsers() {
      return this.users.filter((user) => ["ADMIN", "OWNER"].includes(user.user_access));
    },
    kpis() {
      let cuadrados = 0;
      let sobrantes = 0;
      let faltantes = 0;
      for (const r of this.rows) {
        const d = this.diffOf(r);
        if (d === 0) cuadrados++;
        else if (d > 0) sobrantes++;
        else faltantes++;
      }
      return { cuadrados, sobrantes, faltantes };
    },
    adjustDirection() {
      // 'in' = sobrante (stockIn), 'out' = faltante (stockOut)
      if (!this.adjustRow) return "in";
      return this.diffOf(this.adjustRow) > 0 ? "in" : "out";
    },
    filteredMovements() {
      if (this.movFilter === "all" || !this.movFilter) return this.movements;
      return this.movements.filter((m) => this.getEventCategory(m.order_number) === this.movFilter);
    },
    reportWithDiff() {
      return this.verifiedToday.filter(
        (r) => (parseInt(r.cantidad, 10) || 0) !== (parseInt(r.inventario, 10) || 0)
      ).length;
    },
    auditPct() {
      if (!this.storeStockCount) return 0;
      return Math.min(100, Math.round((this.verifiedToday.length / this.storeStockCount) * 100));
    },
    auditPctColor() {
      const p = this.auditPct;
      if (p >= 80) return "success";
      if (p >= 50) return "warning";
      return "error";
    },
    verifiedEanSet() {
      return new Set(this.verifiedToday.map((r) => String(r.ean)));
    },
    pendingItems() {
      const done = this.verifiedEanSet;
      return this.storeItems.filter((i) => !done.has(String(i.ean)));
    },
    pendingCount() {
      return this.pendingItems.length;
    },
  },

  watch: {
    activeTab(tab) {
      // Al abrir el tab de reporte (índice 2), cargar datos una vez
      if (tab === 2 && this.verifiedToday.length === 0 && !this.reportLoading) {
        this.loadReport();
      }
    },
  },

  mounted() {
    this.FP_in = (window.settings && window.settings["AJ-ENTRADA"]) || [];
    this.FP_out = (window.settings && window.settings["AJ-SALIDA"]) || [];
    this.today = getToday();
    this.auditFrom = this.loadAuditFrom();
    this.reportDateFrom = this.auditFrom;
    this.reportDateTo = this.today;
    this.setDefaultDates();
    this.get_users();
    this.loadAuditedInPeriod();
    this.focusBarcode();
  },

  methods: {
    // ---------- Utilities ----------
    showMessage(text, color = "info") {
      this.snackbar = { show: true, text, color, timeout: 3000 };
    },
    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return (
        "$" +
        parseFloat(value).toLocaleString("es", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      );
    },
    focusBarcode() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus();
        }, 50);
      });
    },
    diffOf(row) {
      if (!row) return 0;
      return (parseInt(row.countedQty, 10) || 0) - (parseInt(row.systemQty, 10) || 0);
    },
    diffColor(row) {
      const d = this.diffOf(row);
      if (d === 0) return "success";
      return d > 0 ? "warning" : "error";
    },
    diffLabel(row) {
      const d = this.diffOf(row);
      if (d === 0) return "Cuadrado";
      return d > 0 ? `Sobrante +${d}` : `Faltante ${d}`;
    },
    /** Costo total = costo unitario × cantidad (entera). */
    costTotal(costo, qty) {
      return (parseFloat(costo) || 0) * (parseInt(qty, 10) || 0);
    },
    /** Usuario de la sesión que está auditando (nombre, o correo si no hay nombre). */
    auditorName() {
      const p = window.profile || {};
      return String(p.user_name || p.user_email || "").trim();
    },
    auditorEmail() {
      return String((window.profile && window.profile.user_email) || "").trim();
    },

    // ---------- Scanning ----------
    onScan() {
      const code = (this.barcode || "").trim();
      this.barcode = "";
      if (code) this.lookupProduct(code);
      this.focusBarcode();
    },
    queryMaster(field, value, cb) {
      const qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: [
          { field: "id_pos", operator: "=", value: window.store.store_id },
          { field, operator: "=", value },
        ],
      };
      webserver("get_catalog", qry, (data) => {
        cb(Array.isArray(data) && data[0] ? data[0] : null);
      });
    },
    lookupProduct(code) {
      this.loading = true;
      this.queryMaster("ean", code, (byEan) => {
        if (byEan) {
          this.loading = false;
          this.handleFound(byEan);
          return;
        }
        this.queryMaster("refe", code, (byRefe) => {
          this.loading = false;
          if (byRefe) this.handleFound(byRefe);
          else this.showMessage(`Producto con código ${code} no encontrado`, "error");
        });
      });
    },
    handleFound(product) {
      const prior = this.auditedByEan[product.ean] || null;
      const existing = this.rows.find((r) => r.ean === product.ean);
      if (existing) {
        existing.countedQty = (parseInt(existing.countedQty, 10) || 0) + 1;
        // Refresh system stock in case it changed
        existing.systemQty = Math.round(parseFloat(product.cantidad) || 0);
        existing.raw = product;
        existing.verified = false; // el conteo cambió, requiere re-verificación
        existing.priorAudit = prior;
        this.currentEan = existing.ean;
      } else {
        this.rows.unshift({
          ean: product.ean,
          refe: product.refe || "",
          nombre: product.nombre || "",
          marca: product.marca || product.brand || "",
          categoria: product.categoria || product.category || "",
          grupo: product.grupo || product.grupoName || "",
          color: product.color || product.colorName || "",
          talla: product.talla || "",
          precio: parseFloat(product.precio) || 0,
          costo: parseFloat(product.costo) || 0,
          systemQty: Math.round(parseFloat(product.cantidad) || 0),
          countedQty: 1,
          adjusted: false,
          verified: false,
          priorAudit: prior,
          raw: product,
        });
        this.currentEan = product.ean;
      }
      // Avisar si este producto ya fue auditado/ajustado en el período (cualquier caja/sesión)
      if (prior) {
        this.showMessage(
          `⚠️ Ya auditado ${this.priorAuditLabel(prior)} — contado ${prior.cantidad}, ` +
            `sistema ${prior.inventario}` +
            (prior.pos ? ` · ${prior.pos}` : "") +
            (prior.adjusted ? " · ya ajustado" : ""),
          "warning"
        );
      }
    },
    setCount(row, value) {
      const n = parseInt(value, 10);
      row.countedQty = isNaN(n) || n < 0 ? 0 : n;
      // Editing the count means this product is not yet reconciled/verified
      row.adjusted = false;
      row.verified = false;
    },
    removeRow(row) {
      const idx = this.rows.findIndex((r) => r.ean === row.ean);
      if (idx !== -1) this.rows.splice(idx, 1);
      if (this.currentEan === row.ean) this.currentEan = null;
    },
    refreshStock(ean) {
      this.queryMaster("ean", ean, (product) => {
        if (!product) return;
        const row = this.rows.find((r) => r.ean === ean);
        if (row) {
          row.systemQty = Math.round(parseFloat(product.cantidad) || 0);
          row.raw = product;
        }
      });
    },

    // ---------- Verification (persist to `auditoria` table) ----------
    markVerified(row, opts = {}) {
      if (!row || row.verified) return;
      const adjusted = !!opts.adjusted;
      const cantidad = parseInt(row.countedQty, 10) || 0;
      const inventario = parseInt(row.systemQty, 10) || 0;
      // Usuario que audita. `window.seller` es un objeto (no texto), por eso antes
      // la columna `pos` (varchar 45) quedaba vacía en la tabla `auditoria`.
      const pos = this.auditorName().substring(0, 45);
      const data = {
        store_id: window.store.store_id,
        fecha: getToday(),
        pos,
        ean: row.ean,
        nombre: row.nombre,
        refe: row.refe,
        marca: row.marca,
        grupo: row.grupo,
        costo: row.costo,
        price: row.precio,
        cantidad, // conteo físico
        inventario, // stock del sistema al verificar
        // Marcador de origen: 'AJUSTE' si vino de un ajuste, 'CONTEO' si solo se verificó
        lote: adjusted ? "AJUSTE" : "CONTEO",
      };
      webserver("put_table", { table: "auditoria", data: [data] }, (resp) => {
        if (resp && resp.error) {
          this.showMessage("No se pudo registrar la verificación", "error");
          return;
        }
        row.verified = true;
        // Reflejar en el mapa persistido para que un reescaneo lo detecte
        const prev = this.auditedByEan[row.ean];
        this.$set(this.auditedByEan, row.ean, {
          _id: (prev && prev._id) || 0,
          fecha: data.fecha,
          cantidad,
          inventario,
          pos,
          adjusted: adjusted || (prev && prev.adjusted) || false,
        });
      });
    },
    markAllVerified() {
      const pending = this.rows.filter((r) => !r.verified);
      if (pending.length === 0) return;
      pending.forEach((r) => this.markVerified(r));
      this.showMessage(`${pending.length} producto(s) marcados como verificados`, "success");
    },
    /**
     * Carga desde la tabla central `auditoria` los productos auditados en el
     * período (desde `auditFrom` hasta hoy, de cualquier caja/sesión) en un mapa
     * ean -> { _id, fecha, cantidad, inventario, adjusted, pos }.
     * Se usa para avisar al reescanear que un producto ya se auditó/ajustó.
     */
    loadAuditedInPeriod() {
      const today = getToday();
      const from = this.auditFrom && this.auditFrom <= today ? this.auditFrom : today;
      const qry = {
        table: "auditoria",
        filters: [
          { field: "store_id", operator: "=", value: window.store.store_id },
          { field: "fecha", operator: ">=", value: from },
          { field: "fecha", operator: "<=", value: today },
        ],
      };
      webserver("get_table", qry, (data) => {
        const rows = Array.isArray(data) ? data : [];
        const map = {};
        for (const r of rows) {
          const prev = map[r.ean];
          const isAdjust = String(r.lote || "").toUpperCase() === "AJUSTE";
          if (!prev || (parseInt(r.id, 10) || 0) > (prev._id || 0)) {
            map[r.ean] = {
              _id: parseInt(r.id, 10) || 0,
              fecha: String(r.fecha || "").substring(0, 10),
              cantidad: parseInt(r.cantidad, 10) || 0,
              inventario: parseInt(r.inventario, 10) || 0,
              pos: r.pos || "",
              // adjusted = cualquier registro de ese ean en el período marcado como AJUSTE
              adjusted: isAdjust || (prev && prev.adjusted) || false,
            };
          } else if (isAdjust) {
            prev.adjusted = true;
          }
        }
        this.auditedByEan = map;
      });
    },
    /** "hoy" o "el YYYY-MM-DD", según la fecha del registro previo. */
    priorAuditLabel(prior) {
      if (!prior || !prior.fecha) return "en este período";
      const f = String(prior.fecha).substring(0, 10);
      return f === getToday() ? "hoy" : `el ${f}`;
    },

    // ---------- Período de auditoría (inicio recordado por tienda) ----------
    auditFromKey() {
      const sid = (window.store && window.store.store_id) || "";
      return `auditLive_from_${sid}`;
    },
    loadAuditFrom() {
      const today = getToday();
      try {
        const v = localStorage.getItem(this.auditFromKey());
        if (v && /^\d{4}-\d{2}-\d{2}$/.test(v) && v <= today) return v;
      } catch (e) {
        /* localStorage no disponible */
      }
      return today;
    },
    saveAuditFrom() {
      try {
        localStorage.setItem(this.auditFromKey(), this.auditFrom);
      } catch (e) {
        /* localStorage no disponible */
      }
    },
    onAuditFromChange() {
      const today = getToday();
      if (!this.auditFrom || this.auditFrom > today) this.auditFrom = today;
      this.saveAuditFrom();
      // El reporte arranca en la misma fecha del período
      this.reportDateFrom = this.auditFrom;
      this.reportDateTo = today;
      // loadReport() también refresca el mapa de auditados del período
      if (this.verifiedToday.length || this.activeTab === 2) this.loadReport();
      else this.loadAuditedInPeriod();
      this.focusBarcode();
    },

    // ---------- Consolidated report ----------
    loadReport() {
      const today = getToday();
      // Normalizar rango: vacíos -> hoy; invertido -> intercambiar
      if (!this.reportDateFrom) this.reportDateFrom = this.reportDateTo || today;
      if (!this.reportDateTo) this.reportDateTo = today;
      if (this.reportDateFrom > this.reportDateTo) {
        const t = this.reportDateFrom;
        this.reportDateFrom = this.reportDateTo;
        this.reportDateTo = t;
      }
      this.reportLoading = true;
      // 1) Verificados del rango desde la tabla `auditoria`
      const qry = {
        table: "auditoria",
        filters: [
          { field: "store_id", operator: "=", value: window.store?.store_id || window.profile?.store_id },
          { field: "fecha", operator: ">=", value: this.reportDateFrom },
          { field: "fecha", operator: "<=", value: this.reportDateTo },
        ],
      };
      webserver("get_table", qry, (data) => {
        this.reportLoading = false;
        if (data && data.error) {
          this.showMessage("Error al cargar el reporte", "error");
          return;
        }
        const rows = Array.isArray(data) ? data : [];
        // Dedup por ean en todo el rango: quedarse con el registro más reciente (mayor id)
        const byEan = new Map();
        for (const r of rows) {
          const key = String(r.ean);
          const prev = byEan.get(key);
          if (!prev || (parseInt(r.id, 10) || 0) > (parseInt(prev.id, 10) || 0)) {
            byEan.set(key, r);
          }
        }
        this.verifiedToday = Array.from(byEan.values());
      });
      // 2) Denominador + lista de SKUs con stock en la tienda (una sola vez)
      if (!this.storeCountLoaded) this.loadStoreStockCount();
      // 3) Refrescar el mapa de auditados del período (aviso al reescanear)
      this.loadAuditedInPeriod();
    },
    loadStoreStockCount() {
      const qry = {
        store: window.store.store_id,
        table: "items",
        filters: [{ field: "id_pos", operator: "=", value: window.store.store_id }],
      };
      webserver("get_catalog", qry, (data) => {
        const items = Array.isArray(data) ? data : [];
        const withStock = items.filter((i) => (parseFloat(i.cantidad) || 0) > 0);
        // Congelar: puede tener miles de filas; evita reactividad profunda de Vue 2.
        this.storeItems = Object.freeze(withStock);
        this.storeStockCount = withStock.length;
        this.storeCountLoaded = true;
      });
    },
    reportDiff(item) {
      return (parseInt(item.cantidad, 10) || 0) - (parseInt(item.inventario, 10) || 0);
    },
    reportDiffColor(item) {
      const d = this.reportDiff(item);
      if (d === 0) return "success";
      return d > 0 ? "warning" : "error";
    },
    reportDiffLabel(item) {
      const d = this.reportDiff(item);
      if (d === 0) return "Cuadrado";
      return d > 0 ? `Sobrante +${d}` : `Faltante ${d}`;
    },
    exportReport() {
      const isPending = this.reportView === "pending";
      const source = isPending ? this.pendingItems : this.verifiedToday;
      if (source.length === 0) {
        this.showMessage("No hay datos para exportar en esta vista", "info");
        return;
      }
      const exportData = isPending
        ? source.map((i) => {
            const stock = Math.round(parseFloat(i.cantidad) || 0);
            const costo = parseFloat(i.costo) || 0;
            return {
              EAN: i.ean,
              Producto: i.nombre,
              Referencia: i.refe,
              Marca: i.marca || i.brand,
              "Stock sistema": stock,
              "Costo unitario": costo,
              "Costo total": this.costTotal(costo, stock),
            };
          })
        : source.map((r) => {
            const contado = parseInt(r.cantidad, 10) || 0;
            const costo = parseFloat(r.costo) || 0;
            return {
              EAN: r.ean,
              Fecha: r.fecha,
              Producto: r.nombre,
              Referencia: r.refe,
              Marca: r.marca,
              Contado: contado,
              Sistema: parseInt(r.inventario, 10) || 0,
              Diferencia: this.reportDiff(r),
              "Costo unitario": costo,
              "Costo total": this.costTotal(costo, contado),
              "Auditado por": r.pos || "",
            };
          });
      const sheet = isPending ? "Pendientes" : "Verificados";
      // Encabezado con la información de quién genera el archivo
      const store = window.store || {};
      const generadoPor = this.auditorEmail()
        ? `${this.auditorName()} (${this.auditorEmail()})`
        : this.auditorName();
      const rango =
        this.reportDateFrom === this.reportDateTo
          ? this.reportDateFrom
          : `${this.reportDateFrom} a ${this.reportDateTo}`;
      const infoRows = [
        ["Auditoría de inventario", sheet],
        ["Tienda", [store.store_name, store.store_id].filter(Boolean).join(" · ")],
        ["Rango", rango],
        ["Generado por", generadoPor],
        ["Fecha de generación", new Date().toLocaleString("es-CO")],
        [],
      ];
      const ws = XLSX.utils.aoa_to_sheet(infoRows);
      XLSX.utils.sheet_add_json(ws, exportData, { origin: `A${infoRows.length + 1}` });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheet);
      const rangeLabel =
        this.reportDateFrom === this.reportDateTo
          ? this.reportDateFrom
          : `${this.reportDateFrom}_a_${this.reportDateTo}`;
      XLSX.writeFile(wb, `auditoria_${rangeLabel}_${isPending ? "pendientes" : "verificados"}.xlsx`);
    },

    // ---------- Adjustment ----------
    openAdjust(row) {
      if (this.diffOf(row) === 0) return;
      if (!this.selectedAuthorizer) {
        this.showMessage("Primero autoriza la sesión", "warning");
        this.openAuthorizerDialog();
        return;
      }
      this.adjustRow = row;
      this.adjustMotive = null;
      this.adjust_dialog = true;
      this.$nextTick(() => {
        if (this.$refs.adjustForm) this.$refs.adjustForm.resetValidation();
      });
    },
    confirmAdjust() {
      if (this.$refs.adjustForm && !this.$refs.adjustForm.validate()) return;
      if (!this.selectedAuthorizer) {
        this.showMessage("Debes autorizar la sesión", "warning");
        return;
      }
      const row = this.adjustRow;
      const diff = this.diffOf(row);
      if (diff === 0) {
        this.adjust_dialog = false;
        return;
      }

      const direction = diff > 0 ? "in" : "out";
      const cantidad = Math.abs(diff);
      const doc_type = direction === "in" ? "stockIn" : "stockOut";
      const doc_value = cantidad * (row.costo || 0);

      const authorizerClient = {
        PartyIdentification:
          this.selectedAuthorizer.store_id + "-" + this.selectedAuthorizer.user_email,
        PartyName: this.selectedAuthorizer.user_name || this.selectedAuthorizer.user_email,
        client_email: this.selectedAuthorizer.user_email,
        client_phone: "",
        typeid: "13",
      };

      const item = { ...row.raw, cantidad };
      delete item.order_date;
      delete item.temp_quantity;

      const qry = {
        doc: {
          doc_type,
          doc_value,
          store: window.store.store_id,
          userId: window.profile.user_email,
        },
        items: [item],
        paidTo: authorizerClient,
        seller: window.seller,
        store: window.store.store_id,
        payments: [
          {
            payment_code: this.adjustMotive.codigo,
            payment_concept: this.adjustMotive.valor,
            payment_reference: "AUDIT-LIVE",
            payment_value: doc_value,
          },
        ],
      };

      this.savingAdjust = true;
      webserver("put_stock", qry, (data) => {
        this.savingAdjust = false;
        if (data && data.error) {
          this.showMessage("Error al crear el ajuste", "error");
          return;
        }
        row.adjusted = true;
        this.adjust_dialog = false;
        this.showMessage(
          `Ajuste de ${direction === "in" ? "entrada (sobrante)" : "salida (faltante)"} creado`,
          "success"
        );
        // Un ajuste implica que el producto quedó verificado (y ajustado)
        this.markVerified(row, { adjusted: true });
        // Reflect new stock so the difference goes to zero
        this.refreshStock(row.ean);
        // Invalidate cached movements for this product
        delete this.movCache[row.ean];
        this.focusBarcode();
      });
    },

    // ---------- Movements ----------
    setDefaultDates() {
      const to = new Date();
      const from = new Date();
      from.setMonth(from.getMonth() - 6);
      this.dateTo = to.toISOString().substring(0, 10);
      this.dateFrom = from.toISOString().substring(0, 10);
    },
    addDaysYMD(ymd, days) {
      const [y, m, d] = String(ymd).split("-").map(Number);
      const dt = new Date(y, m - 1, d + days);
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return `${dt.getFullYear()}-${mm}-${dd}`;
    },
    showMovements(row) {
      this.movEan = row.ean;
      this.movName = row.nombre;
      this.activeTab = 1;
      this.loadMovements(row.ean);
    },
    reloadMovements() {
      if (!this.movEan) return;
      delete this.movCache[this.movEan];
      this.loadMovements(this.movEan);
    },
    loadMovements(ean) {
      const key = String(ean);
      if (this.movCache[key]) {
        this.movements = this.movCache[key];
        return;
      }
      this.movLoading = true;
      const qry = {
        store: window.store?.store_id || window.profile?.store_id,
        dateFrom: this.dateFrom,
        // order_date es DATETIME: dateTo + 1 dia para incluir los movimientos de hoy
        dateTo: this.addDaysYMD(this.dateTo, 1),
        // El backend filtra por left(order_number,2): 'DI' cubre los DID-
        // (put_stock: entradas/salidas, ajustes, inventario dinamico)
        events: "'OP','OC','AJ','CP','PS','PE','DV','CD','TB','SI','SO','GI','GS','DI'",
      };
      webserver("get_events", qry, (data) => {
        this.movLoading = false;
        if (data && data.error) {
          this.showMessage("Error al cargar movimientos", "error");
          return;
        }
        const byEan = groupEventsByEan(Array.isArray(data) ? data : []);
        const evs = (byEan.get(key) || []).map((e) => {
          const q = parseFloat(e.quantity || 0);
          return {
            ...e,
            quantity: q,
            tcost: q * parseFloat(e.costo || 0),
            tprice: q * parseFloat(e.price || 0),
          };
        });
        evs.sort((a, b) => new Date(b.order_date) - new Date(a.order_date));
        if (evs.length) this.movCache[key] = evs;
        this.movements = evs;
      });
    },
    getEventName(orderNumber, qty) {
      const { code } = classifyEvent(orderNumber);
      // DID: put_stock registra entradas y salidas con la cantidad firmada
      if (code === "DID") return (parseFloat(qty) || 0) < 0 ? "Salida Inv." : "Entrada Inv.";
      const names = {
        OP: "Venta (OP)",
        OC: "Compra (OC)",
        AJ: "Ajuste",
        CP: "Cambio de Precio",
        SI: "Entrada Inv.",
        SO: "Salida Inv.",
        SAL: "Salida",
        GI: "Ingreso Garantía",
        GS: "Salida Garantía",
        PS: "Salida Promoción",
        PE: "Entrada Promoción",
        DV: "Devolución",
        CD: "Cambio de Documento",
        TB: "Traslado Bodega",
      };
      return names[code] || code || orderNumber;
    },
    getEventIcon(orderNumber, qty) {
      const { code } = classifyEvent(orderNumber);
      if (code === "DID") {
        return (parseFloat(qty) || 0) < 0 ? "mdi-package-variant-minus" : "mdi-package-variant-plus";
      }
      const icons = {
        OP: "mdi-file-document",
        OC: "mdi-cart",
        AJ: "mdi-adjust",
        CP: "mdi-tag",
        SI: "mdi-package-variant-plus",
        SO: "mdi-package-variant-minus",
        SAL: "mdi-package-variant-minus",
        GI: "mdi-shield-plus",
        GS: "mdi-shield-remove",
        PS: "mdi-sale",
        PE: "mdi-percent",
        DV: "mdi-undo",
        CD: "mdi-file-swap",
        TB: "mdi-truck",
      };
      return icons[code] || "mdi-file";
    },
    getEventColor(orderNumber, qty) {
      const { code } = classifyEvent(orderNumber);
      if (code === "DID") return (parseFloat(qty) || 0) < 0 ? "red" : "green";
      const colors = {
        OP: "primary",
        OC: "success",
        AJ: "warning",
        CP: "info",
        SI: "green",
        SO: "red",
        SAL: "red",
        GI: "orange",
        GS: "deep-orange",
        PS: "purple",
        PE: "indigo",
        DV: "error",
        CD: "blue-grey",
        TB: "teal",
      };
      return colors[code] || "grey";
    },
    getEventCategory(orderNumber) {
      const { code } = classifyEvent(orderNumber);
      if (code === "OC") return "Compra";
      if (code === "OP") return "Venta";
      if (["AJ", "SI", "SO", "SAL", "TB", "DID"].includes(code)) return "Ajuste";
      return "Otro";
    },

    // ---------- Authorization (copied pattern from IAOut.vue) ----------
    async get_users() {
      const qry = {
        store: window.profile.store_id,
        table: "users",
        filters: [
          { field: "store_id", operator: "=", value: window.profile.store_id },
          { field: "user_access", operator: " IN ", value: "'ADMIN','OWNER'" },
        ],
      };
      this.loading_users = true;
      try {
        const result = await new Promise((resolve, reject) => {
          webserver(
            "get_table",
            qry,
            (data) => resolve(data),
            () => reject([])
          );
        });
        this.users = result || [];
        if (this.users.length === 0) {
          this.showMessage("No se encontraron usuarios autorizados", "warning");
        }
      } catch (error) {
        this.users = [];
        this.showMessage("Error al cargar usuarios", "error");
      } finally {
        this.loading_users = false;
      }
    },
    openAuthorizerDialog() {
      this.authorizer_dialog = true;
      if (this.users.length === 0) this.get_users();
    },
    selectAuthorizerForValidation(user) {
      this.tempAuthorizer = user;
      this.authorizerPassword = "";
      this.passwordError = "";
      this.password_dialog = true;
    },
    closeAuthorizerDialog() {
      this.authorizer_dialog = false;
      this.tempAuthorizer = null;
    },
    cancelPasswordValidation() {
      this.password_dialog = false;
      this.authorizerPassword = "";
      this.passwordError = "";
      this.tempAuthorizer = null;
    },
    validatePassword() {
      if (!this.authorizerPassword) {
        this.passwordError = "La contraseña es requerida";
        return;
      }
      this.validatingPassword = true;
      const md5Password = this.md5(this.authorizerPassword);
      if (this.tempAuthorizer.user_password === md5Password) {
        this.selectedAuthorizer = this.tempAuthorizer;
        this.password_dialog = false;
        this.authorizer_dialog = false;
        this.authorizerPassword = "";
        this.passwordError = "";
        this.showMessage(
          `Autorizado por ${
            this.selectedAuthorizer.user_name || this.selectedAuthorizer.user_email
          }`,
          "success"
        );
        this.tempAuthorizer = null;
      } else {
        this.passwordError = "Contraseña incorrecta";
        this.authorizerPassword = "";
      }
      this.validatingPassword = false;
    },
    md5(string) {
      function md5cycle(x, k) {
        var a = x[0],
          b = x[1],
          c = x[2],
          d = x[3];
        a = ff(a, b, c, d, k[0], 7, -680876936);
        d = ff(d, a, b, c, k[1], 12, -389564586);
        c = ff(c, d, a, b, k[2], 17, 606105819);
        b = ff(b, c, d, a, k[3], 22, -1044525330);
        a = ff(a, b, c, d, k[4], 7, -176418897);
        d = ff(d, a, b, c, k[5], 12, 1200080426);
        c = ff(c, d, a, b, k[6], 17, -1473231341);
        b = ff(b, c, d, a, k[7], 22, -45705983);
        a = ff(a, b, c, d, k[8], 7, 1770035416);
        d = ff(d, a, b, c, k[9], 12, -1958414417);
        c = ff(c, d, a, b, k[10], 17, -42063);
        b = ff(b, c, d, a, k[11], 22, -1990404162);
        a = ff(a, b, c, d, k[12], 7, 1804603682);
        d = ff(d, a, b, c, k[13], 12, -40341101);
        c = ff(c, d, a, b, k[14], 17, -1502002290);
        b = ff(b, c, d, a, k[15], 22, 1236535329);
        a = gg(a, b, c, d, k[1], 5, -165796510);
        d = gg(d, a, b, c, k[6], 9, -1069501632);
        c = gg(c, d, a, b, k[11], 14, 643717713);
        b = gg(b, c, d, a, k[0], 20, -373897302);
        a = gg(a, b, c, d, k[5], 5, -701558691);
        d = gg(d, a, b, c, k[10], 9, 38016083);
        c = gg(c, d, a, b, k[15], 14, -660478335);
        b = gg(b, c, d, a, k[4], 20, -405537848);
        a = gg(a, b, c, d, k[9], 5, 568446438);
        d = gg(d, a, b, c, k[14], 9, -1019803690);
        c = gg(c, d, a, b, k[3], 14, -187363961);
        b = gg(b, c, d, a, k[8], 20, 1163531501);
        a = gg(a, b, c, d, k[13], 5, -1444681467);
        d = gg(d, a, b, c, k[2], 9, -51403784);
        c = gg(c, d, a, b, k[7], 14, 1735328473);
        b = gg(b, c, d, a, k[12], 20, -1926607734);
        a = hh(a, b, c, d, k[5], 4, -378558);
        d = hh(d, a, b, c, k[8], 11, -2022574463);
        c = hh(c, d, a, b, k[11], 16, 1839030562);
        b = hh(b, c, d, a, k[14], 23, -35309556);
        a = hh(a, b, c, d, k[1], 4, -1530992060);
        d = hh(d, a, b, c, k[4], 11, 1272893353);
        c = hh(c, d, a, b, k[7], 16, -155497632);
        b = hh(b, c, d, a, k[10], 23, -1094730640);
        a = hh(a, b, c, d, k[13], 4, 681279174);
        d = hh(d, a, b, c, k[0], 11, -358537222);
        c = hh(c, d, a, b, k[3], 16, -722521979);
        b = hh(b, c, d, a, k[6], 23, 76029189);
        a = hh(a, b, c, d, k[9], 4, -640364487);
        d = hh(d, a, b, c, k[12], 11, -421815835);
        c = hh(c, d, a, b, k[15], 16, 530742520);
        b = hh(b, c, d, a, k[2], 23, -995338651);
        a = ii(a, b, c, d, k[0], 6, -198630844);
        d = ii(d, a, b, c, k[7], 10, 1126891415);
        c = ii(c, d, a, b, k[14], 15, -1416354905);
        b = ii(b, c, d, a, k[5], 21, -57434055);
        a = ii(a, b, c, d, k[12], 6, 1700485571);
        d = ii(d, a, b, c, k[3], 10, -1894986606);
        c = ii(c, d, a, b, k[10], 15, -1051523);
        b = ii(b, c, d, a, k[1], 21, -2054922799);
        a = ii(a, b, c, d, k[8], 6, 1873313359);
        d = ii(d, a, b, c, k[15], 10, -30611744);
        c = ii(c, d, a, b, k[6], 15, -1560198380);
        b = ii(b, c, d, a, k[13], 21, 1309151649);
        a = ii(a, b, c, d, k[4], 6, -145523070);
        d = ii(d, a, b, c, k[11], 10, -1120210379);
        c = ii(c, d, a, b, k[2], 15, 718787259);
        b = ii(b, c, d, a, k[9], 21, -343485551);
        x[0] = add32(a, x[0]);
        x[1] = add32(b, x[1]);
        x[2] = add32(c, x[2]);
        x[3] = add32(d, x[3]);
      }
      function cmn(q, a, b, x, s, t) {
        a = add32(add32(a, q), add32(x, t));
        return add32((a << s) | (a >>> (32 - s)), b);
      }
      function ff(a, b, c, d, x, s, t) {
        return cmn((b & c) | (~b & d), a, b, x, s, t);
      }
      function gg(a, b, c, d, x, s, t) {
        return cmn((b & d) | (c & ~d), a, b, x, s, t);
      }
      function hh(a, b, c, d, x, s, t) {
        return cmn(b ^ c ^ d, a, b, x, s, t);
      }
      function ii(a, b, c, d, x, s, t) {
        return cmn(c ^ (b | ~d), a, b, x, s, t);
      }
      function md51(s) {
        var n = s.length,
          state = [1732584193, -271733879, -1732584194, 271733878],
          i;
        for (i = 64; i <= s.length; i += 64) {
          md5cycle(state, md5blk(s.substring(i - 64, i)));
        }
        s = s.substring(i - 64);
        var tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        for (i = 0; i < s.length; i++) tail[i >> 2] |= s.charCodeAt(i) << (i % 4 << 3);
        tail[i >> 2] |= 0x80 << (i % 4 << 3);
        if (i > 55) {
          md5cycle(state, tail);
          for (i = 0; i < 16; i++) tail[i] = 0;
        }
        tail[14] = n * 8;
        md5cycle(state, tail);
        return state;
      }
      function md5blk(s) {
        var md5blks = [],
          i;
        for (i = 0; i < 64; i += 4) {
          md5blks[i >> 2] =
            s.charCodeAt(i) +
            (s.charCodeAt(i + 1) << 8) +
            (s.charCodeAt(i + 2) << 16) +
            (s.charCodeAt(i + 3) << 24);
        }
        return md5blks;
      }
      var hex_chr = "0123456789abcdef".split("");
      function rhex(n) {
        var s = "",
          j = 0;
        for (; j < 4; j++)
          s += hex_chr[(n >> (j * 8 + 4)) & 0x0f] + hex_chr[(n >> (j * 8)) & 0x0f];
        return s;
      }
      function hex(x) {
        for (var i = 0; i < x.length; i++) x[i] = rhex(x[i]);
        return x.join("");
      }
      function add32(a, b) {
        return (a + b) & 0xffffffff;
      }
      return hex(md51(string));
    },
  },
};
</script>

<style scoped>
.minimal-table >>> th {
  font-weight: 600;
}
.centered-input >>> input {
  text-align: center;
}
</style>
